import "swiper/css";

import { UseQueryResult } from "@tanstack/react-query";
import { useState } from "react";
import SwiperCore from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import { CarouselArrows, updateArrowStatus } from "~/components/editorials/carousel-utils/carousel-utils";
import {
  filterSocialWallData,
  SocialWallCarousel,
  SocialWallMissingData,
  SocialWallSkeleton,
  useSocialWallQuery,
} from "~/components/editorials/social-wall-utils/social-wall-utils";
import { SocialWallResponse } from "~/shared/social-wall-response.interface";
import Constants from "~/utils/constants";

import SocialDialog from "../social-dialog";
import styles from "./mosaic-social-wall.module.scss";

type Props = {
  socialIDs: string[];
};

export default function MosaicSocialWall({ socialIDs }: Props) {
  const MAXIMUM_SLIDES = 3;

  const [socialWallIndex, setSocialWallIndex] = useState(0);
  const [socialWallDialogOpened, setSocialWallDialogOpened] = useState(false);
  const [arrowsStatus, setArrowsStatus] = useState({
    arrowL: false,
    arrowR: socialIDs.length > MAXIMUM_SLIDES,
  });
  const [swiperInstance, setSwiperInstance] = useState<SwiperCore>();
  const { data: socialWallRawData, isLoading: isSocialWallLoading }: UseQueryResult<SocialWallResponse> =
    useSocialWallQuery(["tiktok", "instagram"]);

  const onClose = () => {
    setSocialWallDialogOpened(false);
  };

  const socialWallData = filterSocialWallData(socialWallRawData, socialIDs);

  return (
    <>
      <div className={styles.carouselContainer}>
        <Swiper
          onSwiper={setSwiperInstance}
          onTransitionEnd={(currentSwiper) =>
            socialIDs.length > MAXIMUM_SLIDES ? setArrowsStatus(updateArrowStatus(currentSwiper)) : null
          }
          slidesPerView={1.2}
          spaceBetween={12}
          speed={Constants.SWIPER_TRANSITION_DURATION}
          breakpoints={{
            640: {
              slidesPerView: 2.2,
            },
            900: {
              slidesPerView: MAXIMUM_SLIDES,
            },
          }}
        >
          {socialWallData ? (
            socialWallData.map((data, index) => (
              <SwiperSlide key={data.id} className={styles.swiperSlide}>
                <SocialWallCarousel
                  data={data}
                  index={index}
                  setSocialWallDialogOpened={setSocialWallDialogOpened}
                  socialWallDialogOpened={socialWallDialogOpened}
                  setSocialWallIndex={setSocialWallIndex}
                />
              </SwiperSlide>
            ))
          ) : isSocialWallLoading ? (
            Array.from({ length: MAXIMUM_SLIDES }).map((_, i) => (
              <SwiperSlide key={i} className={styles.initialSlide}>
                <SocialWallSkeleton />
              </SwiperSlide>
            ))
          ) : (
            <SocialWallMissingData />
          )}
        </Swiper>
        {swiperInstance && socialWallData ? (
          <CarouselArrows swiperInstance={swiperInstance} arrowsStatus={arrowsStatus} className={styles.arrows} />
        ) : null}
      </div>
      <SocialDialog
        isOpen={socialWallDialogOpened}
        onClose={onClose}
        externalPostId={socialWallData?.[socialWallIndex]?.external_post_id ?? ""}
        name={socialWallData?.[socialWallIndex]?.external_name ?? ""}
        type={socialWallData?.[socialWallIndex]?.type ?? ""}
        postLink={socialWallData?.[socialWallIndex]?.post_link ?? ""}
      />
    </>
  );
}
